import {
	Alert,
	AlertDescription,
	AlertIcon,
	AlertTitle,
	Box,
	Button,
	Modal,
	ModalBody,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Text,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from 'hooks/usePermissions';
import { clearAdTokenBlock } from '../../redux/utilSlice';
import { adTokenCopy, platformInfo } from 'utils/adTokenBlock';

// Where the Secrets screen lives (Admin Settings → Configuration). The same path
// the Meta WhatsApp settings screen sends administrators to.
const SECRETS_PATH = '/admin-setting/configuration';

/**
 * Shown when the server refuses a lead-status change because the access token of
 * the ad platform that lead came from (Meta, TikTok, Google Ads, Snapchat…) is
 * unusable. Mounted ONCE (src/index.js); any status control opens it through
 * `reportAdTokenBlock`, and the slice ignores a second open while this one is
 * showing — so three controls failing together is one modal.
 *
 * Platform-agnostic: the wording comes from the refusal's platform and kind
 * (utils/adTokenBlock.js), so a new platform needs no change here. It explains the
 * real problem, says plainly that the status was NOT changed, and tells an
 * administrator where to fix it or everyone else to ask one. The overlay does not
 * dismiss it: it needs an explicit acknowledgement.
 */
const AdTokenBlockModal = () => {
	const block = useSelector((state) => state.util?.adTokenBlock);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { hasPermission } = usePermissions();

	if (!block) return null;

	const copy = adTokenCopy(block.kind, block.platform);
	const { secrets } = platformInfo(block.platform);
	// Only people who can open the Secrets screen get a button to it.
	const isAdmin = hasPermission('admin_settings');
	const showSettings = copy.adminCanFix && isAdmin;

	const close = () => dispatch(clearAdTokenBlock());
	const openSettings = () => {
		close();
		navigate(SECRETS_PATH);
	};

	return (
		<Modal
			isOpen
			onClose={close}
			isCentered
			size='lg'
			closeOnOverlayClick={false}
		>
			<ModalOverlay />
			<ModalContent borderRadius='md' boxShadow='lg' bg='bg.surface'>
				<ModalHeader p={0}>
					<Alert status='error' borderRadius='md' alignItems='flex-start'>
						<AlertIcon boxSize='20px' mt={1} />
						<Box>
							<AlertTitle fontSize='lg' mb={1} color='text.heading'>
								{copy.title}
							</AlertTitle>
							<AlertDescription fontSize='md' color='text.secondary'>
								The status change was not performed.
							</AlertDescription>
						</Box>
					</Alert>
				</ModalHeader>

				<ModalBody pt={4}>
					<Text color='text.primary' mb={4}>
						{copy.detail}
					</Text>

					<Box
						borderLeftWidth='3px'
						borderColor='accent.solid'
						bg='bg.subtle'
						borderRadius='sm'
						px={3}
						py={2}
					>
						<Text
							fontSize='xs'
							fontWeight='bold'
							letterSpacing='wide'
							textTransform='uppercase'
							color='text.muted'
							mb={1}
						>
							What to do
						</Text>
						<Text color='text.primary'>
							{!copy.adminCanFix
								? 'Try again in a moment.'
								: isAdmin
									? `Update or reconnect the integration in Admin Settings → Secrets (${secrets}), then try again.`
									: 'Ask an administrator to update or reconnect the integration, then try again.'}
						</Text>
					</Box>
				</ModalBody>

				<ModalFooter gap={3}>
					{showSettings ? (
						<>
							<Button variant='outline' onClick={close}>
								Close
							</Button>
							<Button colorScheme='brand' onClick={openSettings}>
								Open Admin Settings
							</Button>
						</>
					) : (
						<Button colorScheme='brand' onClick={close}>
							OK
						</Button>
					)}
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

export default AdTokenBlockModal;
